import type { Vec2 } from "./types.js";

export const HOOP: Vec2 = { x: 0.5, y: 0.08 };

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function subtract(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(vector: Vec2, amount: number): Vec2 {
  return { x: vector.x * amount, y: vector.y * amount };
}

export function length(vector: Vec2): number {
  return Math.hypot(vector.x, vector.y);
}

export function distance(a: Vec2, b: Vec2): number {
  return length(subtract(a, b));
}

export function normalize(vector: Vec2): Vec2 {
  const magnitude = length(vector);
  return magnitude === 0 ? { x: 0, y: 0 } : scale(vector, 1 / magnitude);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function lerp(a: Vec2, b: Vec2, amount: number): Vec2 {
  return { x: a.x + (b.x - a.x) * amount, y: a.y + (b.y - a.y) * amount };
}

export function smoothstep(value: number): number {
  const t = clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
}

export function distanceToSegment(point: Vec2, start: Vec2, end: Vec2): number {
  const segment = subtract(end, start);
  const denominator = segment.x * segment.x + segment.y * segment.y;
  if (denominator === 0) return distance(point, start);
  const relative = subtract(point, start);
  const projection = clamp(
    (relative.x * segment.x + relative.y * segment.y) / denominator,
    0,
    1,
  );
  return distance(point, add(start, scale(segment, projection)));
}

export function shotValue(position: Vec2): 2 | 3 {
  return distance(position, HOOP) >= 0.52 || (position.y < 0.53 && Math.abs(position.x - 0.5) > 0.29)
    ? 3
    : 2;
}

export function inBounds(position: Vec2): boolean {
  return position.x >= 0.055 && position.x <= 0.945 && position.y >= 0.075 && position.y <= 0.94;
}
